/**
 * LEGAL'S DISCLAIMER OPTIONS ARE THE SOURCE OF THE WORDING (2026-10-28).
 *
 * Disclaimer Builder in Email Hub sync, 2026-10-27: the helper pulls straight from the legal
 * base, no in-between copy. The backend already reads Legal Reviews › Disclaimer Options
 * (`GET /disclaimer/options`, built for the disclaimer engine). Each item in
 * disclaimer-rules.json carries `legalId`, the record it maps to there; once that table has
 * been read, legal's Disclaimer Copy replaces the bundled `copy` for the item.
 *
 * What stays ours, on purpose:
 *   · which words tag an item (legal's Trigger Phrases are hints for the Legal AI Tool and
 *     caught 3% of the disclaimers that shipped in 1,100 sent eblasts; ours caught 77% —
 *     audit _changesets/DISCLAIMER-TRIGGER-AUDIT-20261027.md);
 *   · any line whose legal copy still has a fill-in bracket ("[Select applicable: …]",
 *     "[Grand Opening]", "[LM Branch Licensing here]") — the app builds those from the
 *     follow-up answers, so the bundled text is used until the bracket can be filled.
 *
 * If the table can't be read (offline, 401, timeout), nothing changes: the bundled copy is
 * used, exactly as before. No React here.
 */

export interface LegalOptionRecord { id: string; fields?: Record<string, unknown> }
export interface LegalOption { id: string; name: string; type: string; copy: string }

let LEGAL: Record<string, LegalOption> = {};
let loadedAt = "";
const listeners = new Set<() => void>();

/** legal's copy as plain text: markdown links → their label, bare <urls> and \* escapes removed */
export function cleanLegalCopy(raw: string): string {
  return String(raw || "")
    .replace(/\[([^\]]+)\]\((https?:[^)]+)\)(?:\s*-\s*https?:\S+)?/g, "$1")   // [text](url) and "[text](url)-url"
    .replace(/\s*\((https?:[^)\s]+)\)/g, "")                                   // " (https://…)"
    .replace(/<https?:[^>]+>/g, "")                                             // <https://…>
    .replace(/\\([*_[\]])/g, "$1")                                              // \* → *
    .replace(/\s+/g, " ")
    .trim();
}

/** a bracket left for someone to fill: "[Select applicable: …]", "[INSERT DATE]", "[Grand Opening]" */
export function hasFillIn(copy: string): boolean {
  return /\[[^\]]+\]/.test(copy);
}

/** store what GET /disclaimer/options returned. Returns how many records were usable. */
export function setLegalOptions(records: LegalOptionRecord[] | null | undefined): number {
  const next: Record<string, LegalOption> = {};
  (records || []).forEach((r) => {
    if (!r || !/^rec[A-Za-z0-9]{14}$/.test(String(r.id || ""))) return;
    const f = r.fields || {};
    const copy = cleanLegalCopy(typeof f["Disclaimer Copy"] === "string" ? (f["Disclaimer Copy"] as string) : "");
    next[r.id] = {
      id: r.id,
      name: typeof f.Name === "string" ? (f.Name as string).trim() : "",
      type: typeof f["Disclaimer Type"] === "string" ? (f["Disclaimer Type"] as string) : "",
      copy,
    };
  });
  LEGAL = next;
  loadedAt = Object.keys(next).length ? new Date().toISOString() : "";
  listeners.forEach((fn) => { try { fn(); } catch { /* a listener must never break the load */ } });
  return Object.keys(next).length;
}

/** legal's wording for a record, or null when unknown / empty / still has a fill-in bracket */
export function legalCopy(legalId: string | undefined): string | null {
  if (!legalId) return null;
  const o = LEGAL[legalId];
  if (!o || !o.copy || hasFillIn(o.copy)) return null;
  return o.copy;
}

export function legalOption(legalId: string | undefined): LegalOption | null {
  return (legalId && LEGAL[legalId]) || null;
}

/** "" until the table has been read; then when it was */
export function legalLoadedAt(): string { return loadedAt; }

export function onLegalOptions(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** read the table once per session through `get` (api.getDisclaimerOptions). Never throws. */
let inflight: Promise<number> | null = null;
export function loadLegalOptions(get: () => Promise<{ records?: LegalOptionRecord[] }>): Promise<number> {
  if (loadedAt) return Promise.resolve(Object.keys(LEGAL).length);
  if (inflight) return inflight;
  inflight = get()
    .then((r) => setLegalOptions(r && r.records))
    .catch(() => 0)
    .finally(() => { inflight = null; });
  return inflight;
}

/** test seam */
export function resetLegalOptions(): void { LEGAL = {}; loadedAt = ""; inflight = null; }
